import React, { useEffect, useState } from 'react';
import { Calendar, Image as ImageIcon, Home } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Database } from '../lib/database.types';
import { useNavigate } from 'react-router-dom';

type Event = Database['public']['Tables']['events']['Row'] & {
  images: Database['public']['Tables']['event_images']['Row'][];
};

export default function Events() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchEvents();
  }, []);

  async function fetchEvents() {
    try {
      // Buscar eventos
      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select('*')
        .order('date', { ascending: true });

      if (eventsError) throw eventsError;

      // Buscar imagens para cada evento
      const eventsWithImages = await Promise.all(
        eventsData.map(async (event) => {
          const { data: imagesData } = await supabase
            .from('event_images')
            .select('*')
            .eq('event_id', event.id);

          return {
            ...event,
            images: imagesData || [],
          };
        })
      );

      setEvents(eventsWithImages);
    } catch (error) {
      console.error('Erro ao buscar eventos:', error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p>Carregando eventos...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Calendar className="h-8 w-8 text-brand-teal" />
          <h1 className="text-3xl font-bold text-brand-teal">Eventos</h1>
        </div>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
        >
          <Home className="h-4 w-4" />
          <span>Voltar para Home</span>
        </button>
      </div>
      
      <div className="grid gap-8">
        {events.map((event) => (
          <div key={event.id} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
            {event.images.length > 0 && (
              <div className="relative aspect-video">
                <img
                  src={event.images[0].image_url}
                  alt={event.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            <div className="p-6">
              <h3 className="text-xl font-semibold text-brand-teal mb-2">{event.title}</h3>
              <div className="text-gray-600 space-y-2">
                <p><strong>Data:</strong> {new Date(event.date).toLocaleDateString('pt-BR')}</p>
                <p><strong>Horário:</strong> {event.time}</p>
                {event.description && <p>{event.description}</p>}
              </div>

              {event.images.length > 1 && (
                <div className="mt-4">
                  <h4 className="font-semibold text-brand-teal mb-2 flex items-center gap-2">
                    <ImageIcon className="h-4 w-4" />
                    Galeria de Fotos
                  </h4>
                  <div className="grid grid-cols-4 gap-2">
                    {event.images.slice(1).map((image) => (
                      <div key={image.id} className="aspect-square rounded-lg overflow-hidden">
                        <img
                          src={image.image_url}
                          alt={event.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {events.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Nenhum evento programado no momento.
          </div>
        )}
      </div>
    </div>
  );
}